import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { BRAND_PARAM } from "@/lib/brandPref";

/**
 * Routes whose content is ABOUT one brand. Only these carry `?brand=`.
 *
 * Settings and Billing are deliberately absent: they are workspace-scoped, and
 * hanging a brand id off them would imply a per-brand plan that does not exist.
 * A param that does not mean anything on the page it is on is a promise the app
 * cannot keep — and the next person to read it will assume it filters something.
 */
const BRAND_SCOPED = [
  "/app/dashboard",
  "/app/find-customers",
  "/app/saved-leads",
  "/app/brand",
  "/app/brand-foundation",
];

function isBrandScoped(pathname) {
  return BRAND_SCOPED.some((p) => pathname === p || pathname.startsWith(p + "/"));
}

/**
 * Keeps this TAB's `?brand=` and the seated brand in agreement — the per-tab
 * half of brand selection. Renders nothing; mounted once, in AppShell.
 *
 * ── WHY THE URL AT ALL ─────────────────────────────────────────────────────
 *
 * "Compare two clients side by side" means two tabs holding two different
 * brands at the same time. localStorage cannot do that (one value per origin)
 * and sessionStorage cannot either (a duplicated tab inherits a copy, and every
 * navigation within a tab shares it). The address bar is the only per-tab store
 * a browser gives us — and it survives reload, duplicates correctly, and can be
 * sent to a colleague. So the URL is the record, and this component is the
 * thing that maintains it.
 *
 * ── WHY IT DOES NOT FIGHT ITSELF ───────────────────────────────────────────
 *
 * There are two directions of sync and they are trivially an infinite loop if
 * written naively: the URL sets the brand, seating the brand rewrites the URL,
 * which sets the brand… The guard is `syncedRef`, which remembers the (param,
 * brandId) pair this component itself last put in place. On each run exactly
 * one question is asked — *which side changed since we last agreed?* — and only
 * that side's direction is applied:
 *
 *   · the param moved  → the URL is an instruction. Switch the brand to it.
 *     (Back/forward buttons, a pasted link, a second tab being opened.)
 *   · the brand moved  → the switcher was used. Rewrite the param to match.
 *
 * If neither moved, nothing happens, which is the case on the overwhelming
 * majority of renders.
 *
 * ── COLD LOAD IS NOT HANDLED HERE, ON PURPOSE ──────────────────────────────
 *
 * `AuthContext.refreshBrand` reads `?brand=` itself (via `getBrandParam`) and
 * treats it as the first candidate in the resolution order, so on a fresh load
 * the correct brand is seated BEFORE any brand-scoped effect runs. Doing it
 * here instead would mean seating the default first and correcting afterwards —
 * a real frame of the wrong client's numbers under a URL naming the right one.
 * By the time this component's first effect runs, the two normally already
 * agree and `syncedRef` simply records that.
 */
export default function BrandUrlSync() {
  const location = useLocation();
  const navigate = useNavigate();
  const {
    brand,
    brands,
    brandLoading,
    switchBrand,
    reportBrandGone,
  } = useAuth();

  const syncedRef = useRef({ param: null, brandId: null });
  const scoped = isBrandScoped(location.pathname);

  // NOTE — there is deliberately NO "reset the memory when the tenant changes"
  // branch here, and the omission cost a bug worth recording.
  //
  // The first version cleared `syncedRef` whenever `tenantEpoch` moved, on the
  // theory that a workspace switch invalidates the agreement. But the epoch is
  // ONE counter for workspace AND brand changes (see AuthContext) — so it also
  // moved on every brand switch, blanking the memory. The stale `?brand=` still
  // in the address bar then looked like a fresh instruction, direction 1 fired,
  // and the sync switched the user straight back to the brand they had just
  // left. Clicking brand B did nothing, twice a second, forever.
  //
  // No reset is needed, because a workspace switch resolves correctly without
  // one: the param is unchanged (so direction 1 stays quiet), the newly seated
  // brand differs from the remembered one, and direction 2 rewrites the param
  // to the new workspace's brand. Which is exactly right.
  useEffect(() => {
    if (!scoped) return;
    // Nothing may be decided from an empty or in-flight list: `brands` is []
    // both before the first `/brands` lands and during a workspace switch, and
    // treating that as "the param names nothing" would strip a perfectly valid
    // param and report a brand gone that is merely not loaded yet. Fail closed
    // by doing nothing at all until we actually know.
    if (brandLoading || !brands.length) return;

    const params = new URLSearchParams(location.search);
    const param = params.get(BRAND_PARAM) || null;
    const seatedId = brand?.brand_id || null;
    const prev = syncedRef.current;

    const writeParam = (id) => {
      const next = new URLSearchParams(location.search);
      if (id) next.set(BRAND_PARAM, id);
      else next.delete(BRAND_PARAM);
      // `replace`, never `push`: the brand is a property of where you ARE, not
      // a place you went. Pushing would make Back undo a brand switch one step
      // at a time and turn a morning of comparing two clients into a history
      // stack nobody can get out of.
      navigate({ pathname: location.pathname, search: next.toString() }, { replace: true });
    };

    // ── Direction 1: the URL changed. It wins. ──────────────────────────────
    if (param !== prev.param) {
      if (param && param !== seatedId) {
        if (brands.some((b) => b.brand_id === param)) {
          syncedRef.current = { param, brandId: param };
          switchBrand(param, {
            // The tab's own URL already records this selection. Writing it to
            // the shared preference as well would let opening a link in tab B
            // silently change what tab A resolves to on its next cold load —
            // the side-by-side case quietly breaking itself.
            remember: false,
          }).catch(() => {});
          return;
        }
        // The param names a brand this workspace does not have: deleted, or a
        // link from somewhere it should not have come from. Say so and drop it,
        // rather than showing a different brand's numbers under a URL that
        // plainly asks for this one.
        syncedRef.current = { param: null, brandId: seatedId };
        reportBrandGone?.(param);
        writeParam(seatedId);
        return;
      }
      syncedRef.current = { param, brandId: seatedId };
      if (!param && seatedId) writeParam(seatedId);
      return;
    }

    // ── Direction 2: the seated brand changed. The URL follows. ─────────────
    if (seatedId !== prev.brandId) {
      syncedRef.current = { param: seatedId, brandId: seatedId };
      if (seatedId !== param) writeParam(seatedId);
    }
  }, [
    scoped,
    location.pathname,
    location.search,
    brand,
    brands,
    brandLoading,
    switchBrand,
    reportBrandGone,
    navigate,
  ]);

  return null;
}
